import React, { useMemo, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Grid,
  Group,
  Loader,
  Menu,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  ActionIcon,
  Title,
} from "@mantine/core";
import {
  IconRobot,
  IconPlus,
  IconSparkles,
  IconTrendingUp,
  IconUsers,
  IconBuilding,
  IconWorld,
  IconMail,
  IconTarget,
  IconDots,
  IconPlayerPause,
  IconPlayerPlay,
  IconTrash,
  IconUser,
} from "@tabler/icons-react";
import { useListingLoader } from "../../../components/hooks/ListingHooks";
import { getAgents, deleteAgent } from "../../../api/AgentsAPI";
import { AVATARS, TONES } from "./agent/utils";
import ItemDeleter from "../../../components/TableComponents/ItemDeleter";
import AgentActions from "./AgentActions";
// import { useLanguage } from "../../../stores/LanguageStore";

// --- UTILITAIRES ---
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
};

const extractCompany = (url) => {
  if (!url) return "Entreprise";
  let company = url
    .replace(/^https?:\/\/(www\.)?/, "")
    .split("/")[0]
    .split(".")[0];
  return company.charAt(0).toUpperCase() + company.slice(1);
};

const formatLanguage = (langCode) => {
  const map = { eng: "EN", fra: "FR", spa: "ES", deu: "DE", ita: "IT" };
  return map[langCode?.toLowerCase()] || langCode?.toUpperCase() || "FR";
};

// --- COMPOSANTS DE PRÉSENTATION ---

function StatCard({ icon, label, value }) {
  return (
    <Paper withBorder radius="xl" p="md" bg="var(--mantine-color-gray-0)">
      <Group wrap="nowrap">
        <ThemeIcon size={48} radius="xl" variant="light" color="primary">
          {icon}
        </ThemeIcon>
        <Box>
          <Text size="xl" fw={700} lh={1.1}>
            {value}
          </Text>
          <Text size="xs" c="dimmed" fw={500} tt="uppercase" mt={2}>
            {label}
          </Text>
        </Box>
      </Group>
    </Paper>
  );
}

function Metric({ label, value }) {
  return (
    <Stack gap={2} align="center">
      <Text size="lg" fw={700} c="dark">
        {value.toLocaleString("fr-FR")}
      </Text>
      <Text size="xs" fw={600} c="dimmed" tt="uppercase">
        {label}
      </Text>
    </Stack>
  );
}

function AgentListItem({ agent, onRefresh }) {
  const navigate = useNavigate();
  const active = agent.active;
  const avatar = AVATARS.find((a) => a.id === agent.avatar);
  const displayName = agent.first_name
    ? `${agent.first_name} ${agent.last_name || ""}`.trim()
    : agent.name;
  const companyName = extractCompany(agent.website_url);

  // Fausses données de stats pour la démo visuelle (à remplacer par vos vraies metrics)
  const stats = { sent: 0, replies: 0, meetings: 0 };

  return (
    <Card
      withBorder
      radius="xl"
      p="xl"
      style={{
        transition: "all 0.2s ease",
        borderColor: "var(--mantine-color-gray-3)",
      }}
      shadow="sm"
    >
      <Group wrap="nowrap" align="center">
        {/* IDENTITÉ */}
        <Group wrap="nowrap" gap="md" w={400}>
          <Box pos="relative">
            <Avatar
              src={avatar?.src}
              size={72}
              radius={64}
              style={{ border: "2px solid var(--mantine-color-gray-2)" }}
            >
              <IconUser size={32} color="var(--mantine-color-gray-4)" />
            </Avatar>
            <Box
              pos="absolute"
              bottom={2}
              right={2}
              w={16}
              h={16}
              style={{
                borderRadius: "50%",
                border: "2px solid white",
                backgroundColor: active
                  ? "var(--mantine-color-green-5)"
                  : "var(--mantine-color-gray-4)",
              }}
            />
          </Box>

          <Box style={{ flex: 1, overflow: "hidden" }}>
            <Group gap="xs" wrap="nowrap">
              <Text fw={700} size="xl" truncate>
                {displayName}
              </Text>
              <Badge
                color={active ? "green.1" : "gray.2"}
                c={active ? "green.7" : "gray.6"}
                size="sm"
              >
                {active ? "Actif" : "En pause"}
              </Badge>
            </Group>

            <Group gap={6} mt={2} wrap="nowrap">
              <IconBuilding size={14} color="var(--mantine-color-gray-5)" />
              <Text size="md" c="dimmed" truncate>
                {agent.bio || "SDR IA"} · {companyName}
              </Text>
            </Group>

            <Group gap="xs" mt={8}>
              <Box
                bg={"primary.1"}
                c={"primary"}
                py={4}
                px={8}
                style={{ borderRadius: 8 }}
                fz={12}
              >
                {TONES.find((t) => t.prompt === agent.tone)?.label ||
                  "Tonalité par défaut"}
              </Box>
              <Badge
                variant="outline"
                color="gray"
                radius="sm"
                size="sm"
                leftSection={<IconWorld size={10} />}
              >
                {formatLanguage(agent.spoken_language)}
              </Badge>
            </Group>
          </Box>
        </Group>

        <Group
          wrap="nowrap"
          gap="sm"
          visibleFrom="lg"
          style={{
            flex: 1,
            borderLeft: "1px solid var(--mantine-color-gray-2)",
            paddingLeft: 20,
            maxWidth: "none",
          }}
          mr={64}
        >
          <IconTarget
            size={18}
            color="var(--mantine-color-primary-5)"
            style={{ flexShrink: 0 }}
          />
          <Text size="sm" c="dimmed">
            {agent.brief || "Aucune promesse définie."}
          </Text>
        </Group>

        {/* METRICS */}
        <Group
          gap="xl"
          px="xl"
          py="sm"
          bg="gray.0"
          radius="lg"
          visibleFrom="md"
          mr={64}
        >
          <Metric label="Envois" value={stats.sent} />
          <Metric label="Réponses" value={stats.replies} />
          {/* <Metric label="RDV" value={stats.meetings} /> */}
        </Group>

        {/* ACTIONS & DATE */}
        <Stack align="flex-end" justify="space-between" h={64} gap={"md"}>
          <Text size="xs" c="dimmed">
            Créé le {formatDate(agent.created_on)}
          </Text>

          <Group gap="xs">
            <Button
              variant="light"
              radius="xl"
              size="sm"
              onClick={() => navigate(`/agents/edit/${agent.uniqid}`)}
            >
              Éditer
            </Button>

            <AgentActions current={agent} setLoading={onRefresh} />
          </Group>
        </Stack>
      </Group>
    </Card>
  );
}

// --- PAGE PRINCIPALE ---

const AgentListing = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();
  // const lang = useLanguage((s) => s.language);

  const fetchAgents = () => {
    setLoading(true);
    getAgents().then((res) => {
      if (res?.data?.agents) {
        setRows(res.data.agents.sort((a, b) => b.id - a.id));
      }
      setLoading(false);
    });
  };

  useListingLoader({
    loading,
    setLoading,
    listGetter: getAgents,
    responseField: "agents",
    setRows,
  });

  // Redirection si vide (à adapter selon votre logique métier)
  useEffect(() => {
    if (rows.length === 0 && !loading) {
      navigate(`/agents/create`);
    }
  }, [rows, loading, navigate]);

  const stats = useMemo(() => {
    const active = rows.filter((a) => a.active).length;
    // Si vous récupérez les stats depuis l'API, faites le calcul ici
    const meetings = 0;
    return { total: rows.length, active, meetings };
  }, [rows]);

  return (
    <Stack
      flex={1}
      px={{ base: 16, md: 48 }}
      pt={24}
      pb={48}
      bg="var(--mantine-color-gray-0)"
      minHeight="100vh"
    >
      {/* EN-TÊTE DE LA PAGE */}
      <Group justify="space-between" align="flex-end" mb="md">
        <Box>
          <Title order={1} component="h1" fw={700} m={0}>
            Vos agents commerciaux IA
          </Title>
          <Text mt="xs" opacity={0.7}>
            Retrouvez tous les agents que vous avez créés, suivez leurs
            performances et gérez-les en un coup d'œil.
          </Text>
        </Box>

        <Button
          component={Link}
          to="/agents/create"
          leftSection={<IconPlus size={18} />}
          size="md"
          radius="xl"
          color="primary"
          style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
        >
          Créer un agent
        </Button>
      </Group>

      {/* CARTES DE STATISTIQUES */}
      {rows.length > 0 && (
        <Grid gutter="md" mb="md">
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <StatCard
              icon={<IconUsers size={24} />}
              label="Agents créés"
              value={stats.total}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <StatCard
              icon={<IconRobot size={24} />}
              label="Agents actifs"
              value={stats.active}
            />
          </Grid.Col>
          {/* <Grid.Col span={{ base: 12, sm: 4 }}>
            <StatCard
              icon={<IconTrendingUp size={24} />}
              label="Rendez-vous générés"
              value={stats.meetings}
            />
          </Grid.Col> */}
        </Grid>
      )}

      {/* LISTE DES AGENTS */}
      <Box>
        {loading ? (
          <Center h={300}>
            <Loader color="primary" />
          </Center>
        ) : rows.length > 0 ? (
          <Stack gap="md">
            {rows.map((agent) => (
              <AgentListItem
                key={agent.id || agent.uniqid}
                agent={agent}
                onRefresh={fetchAgents}
              />
            ))}
          </Stack>
        ) : (
          /* ÉTAT VIDE */
          <Paper
            withBorder
            radius="xl"
            p={40}
            bg="white"
            style={{
              borderStyle: "dashed",
              borderColor: "var(--mantine-color-gray-4)",
            }}
          >
            <Stack align="center" ta="center">
              <ThemeIcon size={64} radius="xl" variant="light" color="primary">
                <IconRobot size={32} />
              </ThemeIcon>
              <Text fw={600} size="lg" mt="md">
                Aucun agent pour le moment
              </Text>
              <Text c="dimmed" maw={400}>
                Créez votre premier collaborateur commercial IA en quelques
                minutes grâce à notre assistant guidé.
              </Text>
              <Button
                component={Link}
                to="/agents/create"
                mt="md"
                leftSection={<IconPlus size={16} />}
                radius="xl"
              >
                Créer mon premier agent
              </Button>
            </Stack>
          </Paper>
        )}
      </Box>
    </Stack>
  );
};

export default AgentListing;
